// Extracted from original/src/App.tsx:37934-38815 (the four dialogs of section 12, ADR-08 §2.1 "copy, then cut logic"):
//   open shift "فتح وردية وصندوق جديدة مع تعديل الأرصدة"   :37934-38417
//   close shift "إغلاق وتسوية الوردية"                    :38418-38559
//   open day "تسجيل النقد الافتتاحي لبداية اليوم"          :38560-38681
//   close day "تسوية ومطابقة نهاية اليوم"                   :38682-38815
// Kept: the frames, headers, labels, inputs (`NumericInput` with the "IQD" / "USD" tag), the read-only debts / trusts /
// pending-profit boxes, the expected-figure boxes of the close dialogs, the footer buttons (incl. "تأكيد ومشاركة PDF"
// and "تأكيد وطباعة PDF") and every class string.
// Cut: the stored-balance arithmetic and `executeStartShift` / `handleCloseShift` / `handleStartDayMatch` /
// `handleCloseDayMatch` (App.tsx:1080-1631) → `onSubmit` (the container: PIN → `POST /shifts/open` …).
// Changed by decision: the open dialog no longer edits balances (D-120: "the opening 'edit balances with counter
// account' is removed", old R-S7 / BF created money): it lists the employee's drawers with their book balance
// ("المسند:") and asks the **counted** cash, showing the live difference; the counter-account selector is replaced by
// a notice that the difference is recorded and waits for another user's approval (D-120, D-299); the employee picker
// lists users by id and is locked to the caller without `open_shift_for_other` (D-120, D-298 (e)); the close dialogs
// start empty (no pre-fill with the expected value, D-120); the day-open date is the office's today, read-only (D-302).
// Added: `ApproveDifferenceDialog` and `RejectDifferenceDialog` (D-304, in the style of the C24 cancel dialog).
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, CheckCircle, Clock, Info, Lock, MessageCircle, Printer, ShieldCheck, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NumericInput } from '../components/NumericInput.js';
import { DifferenceHypotheses, type HypothesesState } from '../components/DifferenceHypotheses.js';
import { useI18n } from '../i18n-context.js';

export interface MatchCountValues {
  readonly iqd: string;
  readonly usd: string;
  readonly note: string;
}

export interface MatchDrawerLine {
  readonly name: string;
  readonly usd: boolean;
  /** Book balance (countBasis) preformatted. */
  readonly book: string;
  /** counted − book, preformatted with sign; null when nothing typed or zero. */
  readonly difference: string | null;
  readonly negative: boolean;
}

export interface OpenShiftModalProps {
  readonly open: boolean;
  readonly users: ReadonlyArray<{ readonly id: string; readonly username: string; readonly disabled: boolean }>;
  readonly ownerUserId: string;
  readonly onOwnerChange: (id: string) => void;
  readonly canChooseOwner: boolean;
  /** The employee's two drawers (IQD, USD); null while loading or before a choice. */
  readonly drawers: { readonly iqd: MatchDrawerLine; readonly usd: MatchDrawerLine } | null;
  readonly readOnly: { readonly debts: { iqd: string; usd: string }; readonly trusts: { iqd: string; usd: string }; readonly profits: { iqd: string; usd: string } } | null;
  readonly values: MatchCountValues;
  readonly onChange: (v: MatchCountValues) => void;
  /** Net difference labels when a drawer differs (null = counted matches the book). */
  readonly netDifference: { readonly iqd: string; readonly usd: string } | null;
  readonly busy: boolean;
  readonly onSubmit: (mode: 'open' | 'share' | 'print') => void;
  readonly onClose: () => void;
}

export function OpenShiftModal(p: OpenShiftModalProps) {
  const { t } = useI18n();
  if (!p.open) return null;
  const readOnlyBox = (label: string, v: { iqd: string; usd: string }) => (
    <div className="p-3 bg-white dark:bg-[#161b22] border border-slate-100 dark:border-[#30363d] rounded-xl space-y-1.5 shadow-sm min-w-0">
      <span className="font-bold text-xs text-slate-700 dark:text-[#f0f6fc] block truncate">{label}</span>
      <div className="space-y-1 font-mono">
        <div className="bg-slate-50 dark:bg-[#05070a] px-2 py-1 rounded-lg border border-slate-200/60 dark:border-[#30363d] flex justify-between items-center">
          <span className="text-[9px] font-bold text-slate-400">IQD</span>
          <span className="text-xs font-black text-slate-800 dark:text-white">{v.iqd}</span>
        </div>
        <div className="bg-slate-50 dark:bg-[#05070a] px-2 py-1 rounded-lg border border-slate-200/60 dark:border-[#30363d] flex justify-between items-center">
          <span className="text-[9px] font-bold text-slate-400">USD</span>
          <span className="text-xs font-black text-slate-800 dark:text-white">${v.usd}</span>
        </div>
      </div>
    </div>
  );
  const drawerBox = (w: MatchDrawerLine, value: string, set: (v: string) => void, testId: string) => (
    <div className="p-3 bg-white dark:bg-[#161b22] border border-slate-100 dark:border-[#30363d] rounded-xl sm:rounded-2xl space-y-2 shadow-sm">
      <div className="flex flex-wrap justify-between items-center gap-1">
        <span className="font-black text-xs text-slate-800 dark:text-[#f0f6fc]">{w.name}</span>
        <div className="text-[10px] font-mono text-slate-400 dark:text-slate-400 bg-slate-50 dark:bg-[#0d1117] px-2 py-0.5 rounded-lg border border-slate-100 dark:border-[#30363d]">
          {t('mt.system_label')}{' '}
          {w.usd ? (
            <>
              <span className="font-bold">${w.book}</span> USD
            </>
          ) : (
            <>
              <span className="font-bold">{w.book}</span> د.ع
            </>
          )}
        </div>
      </div>
      <div>
        <div className="relative flex items-center" data-testid={testId}>
          <NumericInput
            value={value}
            onChange={set}
            className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl pl-11 pr-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-right font-mono font-bold text-slate-800 dark:text-white"
            placeholder={t('mt.counted_hint')}
          />
          <span className="absolute left-2.5 text-[10px] font-bold text-slate-400 font-mono pointer-events-none">{w.usd ? 'USD' : 'IQD'}</span>
        </div>
        {w.difference && (
          <p className={`text-[10px] font-mono font-bold mt-0.5 text-right ${!w.negative ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>{w.difference}</p>
        )}
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 md:p-6 z-50" data-testid="open-shift-modal">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white dark:bg-[#0d1117] rounded-3xl sm:rounded-[2rem] border border-slate-200 dark:border-[#30363d] w-full max-w-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl overflow-hidden my-auto"
      >
        {/* Fixed Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-[#30363d] flex justify-between items-center bg-slate-50/90 dark:bg-[#161b22]/90 backdrop-blur-sm shrink-0">
          <h3 className="text-sm sm:text-base font-black text-slate-800 dark:text-white uppercase tracking-tight flex items-center gap-2">
            <Clock className="text-primary-600 dark:text-primary-400 shrink-0" size={18} />
            <span>{t('mt.open_modal_title')}</span>
          </h3>
          <button type="button" onClick={p.onClose} className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors rounded-xl hover:bg-slate-200/50 dark:hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>
        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 custom-scrollbar">
          <form id="new-shift-form" onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {/* Operator selection */}
            <div>
              <label className="block text-xs font-bold text-slate-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">{t('mt.shift_employee')}</label>
              <select
                required
                value={p.ownerUserId}
                disabled={!p.canChooseOwner}
                onChange={(e) => p.onOwnerChange(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-2.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:text-white"
                data-testid="open-shift-owner"
              >
                <option value="">{t('mt.filter_employee')}</option>
                {p.users.map((u) => (
                  <option key={u.id} value={u.id} disabled={u.disabled}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>
            {/* The employee's drawers: book balance and counted cash (D-120) */}
            <div className="space-y-2.5 bg-slate-50/60 dark:bg-[#0d1117]/50 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-[#30363d]/60">
              <span className="text-[10px] font-bold text-slate-400 dark:text-[#8b949e] uppercase tracking-widest block">{t('mt.boxes_opening')}</span>
              {p.drawers && (
                <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1 custom-scrollbar">
                  {drawerBox(p.drawers.iqd, p.values.iqd, (v) => p.onChange({ ...p.values, iqd: v }), 'open-shift-iqd')}
                  {drawerBox(p.drawers.usd, p.values.usd, (v) => p.onChange({ ...p.values, usd: v }), 'open-shift-usd')}
                </div>
              )}
            </div>
            {/* Virtual Accounts: Debts, Trusts & Profits (Read-Only) */}
            {p.readOnly && (
              <div className="space-y-2.5 bg-slate-50/60 dark:bg-[#0d1117]/50 p-3.5 sm:p-4 rounded-2xl border border-slate-100 dark:border-[#30363d]/60">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-bold text-slate-400 dark:text-[#8b949e] uppercase tracking-widest flex items-center gap-1.5">
                    <Lock size={12} className="text-slate-400" />
                    <span>{t('mt.readonly_title')}</span>
                  </span>
                  <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-slate-200/60 dark:bg-slate-800 text-slate-500 dark:text-slate-400">{t('mt.locked')}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {readOnlyBox(t('mt.total_debts'), p.readOnly.debts)}
                  {readOnlyBox(t('mt.total_trusts'), p.readOnly.trusts)}
                  {readOnlyBox(t('mt.pending_profits'), p.readOnly.profits)}
                </div>
              </div>
            )}
            {/* Difference notice (replaces the counter-account selector, D-120) */}
            {p.netDifference && (
              <div className="p-3.5 rounded-2xl bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/80 space-y-2.5 animate-fadeIn" data-testid="open-shift-diff">
                <div className="flex gap-2 items-start">
                  <Info size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="text-xs font-black text-amber-800 dark:text-amber-300">{t('mt.diff_notice_title')}</p>
                    <p className="text-[11px] font-mono text-amber-700 dark:text-amber-400 leading-relaxed dir-ltr text-right">{t('mt.diff_notice', { iqd: p.netDifference.iqd, usd: p.netDifference.usd })}</p>
                  </div>
                </div>
              </div>
            )}
            {/* Notes */}
            <div>
              <label className="block text-xs font-bold text-slate-400 dark:text-[#8b949e] mb-1.5 uppercase tracking-widest">{t('mt.shift_note')}</label>
              <input
                type="text"
                value={p.values.note}
                onChange={(e) => p.onChange({ ...p.values, note: e.target.value })}
                className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:text-white font-medium"
                placeholder={t('mt.shift_note_ph')}
              />
            </div>
          </form>
        </div>
        {/* Fixed Action Footer */}
        <div className="p-3.5 sm:p-4 border-t border-slate-100 dark:border-[#30363d] bg-slate-50/90 dark:bg-[#161b22]/90 backdrop-blur-md shrink-0 space-y-2">
          <button
            type="button"
            disabled={p.busy}
            onClick={() => p.onSubmit('open')}
            data-testid="open-shift-submit"
            className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-primary-600 dark:hover:bg-primary-500 text-white font-black uppercase tracking-widest py-2.5 sm:py-3 rounded-xl transition-all text-xs flex justify-center items-center gap-2 cursor-pointer shadow-md active:scale-[0.99]"
          >
            <CheckCircle size={15} />
            {t('mt.btn_confirm_open')}
          </button>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={p.busy}
              onClick={() => p.onSubmit('share')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-widest py-2.5 rounded-xl transition-all text-xs flex justify-center items-center gap-2 cursor-pointer shadow-sm active:scale-[0.99]"
            >
              <MessageCircle size={14} />
              {t('mt.btn_confirm_share')}
            </button>
            <button
              type="button"
              disabled={p.busy}
              onClick={() => p.onSubmit('print')}
              className="bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest py-2.5 rounded-xl transition-all text-xs flex justify-center items-center gap-2 cursor-pointer shadow-sm active:scale-[0.99]"
            >
              <Printer size={14} />
              {t('mt.btn_confirm_print')}
            </button>
          </div>
          <button
            type="button"
            onClick={p.onClose}
            className="w-full bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-[#8b949e] py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
          >
            {t('cancel')}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export interface CountModalProps {
  readonly open: boolean;
  readonly values: MatchCountValues;
  readonly onChange: (v: MatchCountValues) => void;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
}

export interface CloseShiftModalProps extends CountModalProps {
  readonly expected: { readonly iqd: string; readonly usd: string };
}

const inputCls = 'w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20';
const noteCls = 'w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:text-white';
const labelCls = 'block text-xs font-bold text-slate-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest';
const cancelCls = 'flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-[#161b22] dark:text-[#8b949e] py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all';

function Frame({ title, onClose, children, testId }: { title: string; onClose: () => void; children: ReactNode; testId: string }) {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid={testId}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border border-slate-100 dark:border-[#30363d] w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#161b22]/30">
          <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export function CloseShiftModal(p: CloseShiftModalProps) {
  const { t } = useI18n();
  if (!p.open) return null;
  return (
    <Frame title={t('mt.close_modal_title')} onClose={p.onClose} testId="close-shift-modal">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          p.onSubmit();
        }}
        className="p-6 space-y-4"
      >
        <div className="bg-primary-50/50 dark:bg-primary-950/10 p-4 rounded-2xl border border-primary-100/50 dark:border-primary-900/20 text-xs text-primary-900 dark:text-primary-300 space-y-1.5">
          <p className="font-bold">{t('mt.theo_title')}</p>
          <div className="flex justify-between font-mono">
            <span>{t('mt.expected_iqd')}</span>
            <span className="font-black">{p.expected.iqd} د.ع</span>
          </div>
          <div className="flex justify-between font-mono">
            <span>{t('mt.expected_usd')}</span>
            <span className="font-black">${p.expected.usd}</span>
          </div>
        </div>
        <div data-testid="close-shift-iqd">
          <label className={labelCls}>{t('mt.actual_iqd')}</label>
          <NumericInput required value={p.values.iqd} onChange={(v) => p.onChange({ ...p.values, iqd: v })} className={inputCls} placeholder="0" />
        </div>
        <div data-testid="close-shift-usd">
          <label className={labelCls}>{t('mt.actual_usd')}</label>
          <NumericInput required value={p.values.usd} onChange={(v) => p.onChange({ ...p.values, usd: v })} className={inputCls} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>{t('mt.final_note')}</label>
          <input type="text" value={p.values.note} onChange={(e) => p.onChange({ ...p.values, note: e.target.value })} className={noteCls} placeholder={t('mt.final_note_ph')} />
        </div>
        <div className="pt-4 flex gap-3">
          <button type="submit" disabled={p.busy} data-testid="close-shift-submit" className="flex-1 bg-emerald-600 hover:bg-emerald-50 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs">
            {t('mt.btn_submit_close')}
          </button>
          <button type="button" onClick={p.onClose} className={cancelCls}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </Frame>
  );
}

export interface OpenDayModalProps extends CountModalProps {
  /** The office's today (D-302), shown read-only. */
  readonly dateLabel: string;
}

export function OpenDayModal(p: OpenDayModalProps) {
  const { t } = useI18n();
  if (!p.open) return null;
  return (
    <Frame title={t('mt.day_open_title')} onClose={p.onClose} testId="open-day-modal">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          p.onSubmit();
        }}
        className="p-6 space-y-4"
      >
        <div>
          <label className={labelCls}>{t('mt.day_date')}</label>
          <input type="text" readOnly value={p.dateLabel} dir="ltr" className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xs font-mono font-bold text-right dark:text-white" data-testid="open-day-date" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div data-testid="open-day-iqd">
            <label className={labelCls}>{t('mt.day_start_iqd')}</label>
            <NumericInput required value={p.values.iqd} onChange={(v) => p.onChange({ ...p.values, iqd: v })} className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20" placeholder="0" />
          </div>
          <div data-testid="open-day-usd">
            <label className={labelCls}>{t('mt.day_start_usd')}</label>
            <NumericInput required value={p.values.usd} onChange={(v) => p.onChange({ ...p.values, usd: v })} className="w-full bg-slate-50 dark:bg-[#05070a] border border-slate-200 dark:border-[#30363d] rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/20" placeholder="0" />
          </div>
        </div>
        <div>
          <label className={labelCls}>{t('mt.note')}</label>
          <input type="text" value={p.values.note} onChange={(e) => p.onChange({ ...p.values, note: e.target.value })} className={noteCls} placeholder={t('mt.note_ph')} />
        </div>
        <div className="pt-4 flex gap-3">
          <button type="submit" disabled={p.busy} data-testid="open-day-submit" className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs">
            {t('mt.btn_save_opening')}
          </button>
          <button type="button" onClick={p.onClose} className={cancelCls}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </Frame>
  );
}

export interface CloseDayModalProps extends CountModalProps {
  readonly expected: { readonly iqd: string; readonly usd: string };
}

export function CloseDayModal(p: CloseDayModalProps) {
  const { t } = useI18n();
  if (!p.open) return null;
  return (
    <Frame title={t('mt.day_close_title')} onClose={p.onClose} testId="close-day-modal">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          p.onSubmit();
        }}
        className="p-6 space-y-4"
      >
        <div className="p-4 bg-slate-50 dark:bg-[#070a0e] rounded-xl border border-slate-100 dark:border-[#30363d] text-xs font-mono text-slate-600 dark:text-slate-400 space-y-1">
          <p className="font-bold mb-1 text-slate-700 dark:text-white">{t('mt.day_expected_box')}</p>
          <p>
            {t('mt.day_expected_iqd')} <span className="font-black text-[#f0f6fc]">{p.expected.iqd} د.ع</span>
          </p>
          <p>
            {t('mt.day_expected_usd')} <span className="font-black text-[#f0f6fc]">${p.expected.usd}</span>
          </p>
        </div>
        <div data-testid="close-day-iqd">
          <label className={labelCls}>{t('mt.day_end_iqd')}</label>
          <NumericInput required value={p.values.iqd} onChange={(v) => p.onChange({ ...p.values, iqd: v })} className={inputCls} placeholder="0" />
        </div>
        <div data-testid="close-day-usd">
          <label className={labelCls}>{t('mt.day_end_usd')}</label>
          <NumericInput required value={p.values.usd} onChange={(v) => p.onChange({ ...p.values, usd: v })} className={inputCls} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>{t('mt.note')}</label>
          <input type="text" value={p.values.note} onChange={(e) => p.onChange({ ...p.values, note: e.target.value })} className={noteCls} placeholder={t('mt.final_note_day_ph')} />
        </div>
        <div className="pt-4 flex gap-3">
          <button type="submit" disabled={p.busy} data-testid="close-day-submit" className="flex-1 bg-emerald-600 hover:bg-emerald-50 text-white font-black uppercase tracking-widest py-3 rounded-xl transition-all text-xs">
            {t('mt.btn_save_close_day')}
          </button>
          <button type="button" onClick={p.onClose} className={cancelCls}>
            {t('cancel')}
          </button>
        </div>
      </form>
    </Frame>
  );
}

// ---------------------------------------------------------------------------------------------------------------------
// D-304: approve / reject a stored difference (new; style of the C24 cancel dialog)
// ---------------------------------------------------------------------------------------------------------------------

export interface DifferenceSummary {
  readonly scopeLabel: string;
  readonly responsible: string;
  readonly expected: string;
  readonly counted: string;
  readonly difference: string;
  readonly negative: boolean;
}

export interface ApproveDifferenceDialogProps {
  readonly open: boolean;
  readonly summary: DifferenceSummary | null;
  /** [C34] D-308: the watchdog's explanations of the difference (absent = not shown). */
  readonly hypotheses?: HypothesesState | null;
  readonly onOpenOperation?: (id: string) => void;
  /** Day counts only: custodies of the same vault (D-299 (e)); empty hides the selector. */
  readonly targets: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  readonly target: string;
  readonly onTargetChange: (id: string) => void;
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}

function SummaryBox({ s }: { s: DifferenceSummary }) {
  const { t } = useI18n();
  const row = (label: string, value: string, cls = '') => (
    <div className="flex justify-between">
      <span className="text-gray-500 font-bold">{label}</span>
      <span className={`font-mono font-black ${cls}`} dir="ltr">
        {value}
      </span>
    </div>
  );
  return (
    <div className="rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-100 dark:border-[#30363d] p-3 space-y-1.5 text-xs" data-testid="difference-summary">
      {row(t('mt.pcol_scope'), s.scopeLabel)}
      {row(t('mt.pcol_responsible'), s.responsible)}
      {row(t('mt.pcol_expected'), s.expected)}
      {row(t('mt.pcol_counted'), s.counted)}
      {row(t('mt.pcol_difference'), s.difference, s.negative ? 'text-rose-600' : 'text-emerald-600')}
    </div>
  );
}

export function ApproveDifferenceDialog(p: ApproveDifferenceDialogProps) {
  const { t, locale } = useI18n();
  return (
    <AnimatePresence>
      {p.open && p.summary && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="approve-difference-dialog">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-emerald-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                <ShieldCheck size={36} className="text-emerald-500" />
              </div>
              <h3 className="text-xl font-black text-emerald-600 dark:text-emerald-500">{t('mt.approve_title')}</h3>
            </div>
            <SummaryBox s={p.summary} />
            {p.hypotheses && <DifferenceHypotheses state={p.hypotheses} {...(p.onOpenOperation ? { onOpenOperation: p.onOpenOperation } : {})} />}
            {p.targets.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-black text-gray-500 dark:text-gray-400">{t('mt.approve_post_to')}</label>
                <select value={p.target} onChange={(e) => p.onTargetChange(e.target.value)} className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-2.5 text-xs font-bold dark:text-white" data-testid="approve-target">
                  <option value="">{t('mt.approve_default_target')}</option>
                  {p.targets.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" disabled={p.busy} onClick={p.onConfirm} data-testid="approve-confirm" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-2xl text-xs transition-all disabled:opacity-50">
                {t('mt.btn_approve')}
              </button>
              <button type="button" onClick={p.onClose} className="flex-1 bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-gray-300 font-black py-3 rounded-2xl text-xs">
                {t('cancel')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface RejectDifferenceDialogProps {
  readonly open: boolean;
  readonly summary: DifferenceSummary | null;
  /** [C34] D-308: the same explanations on the reject side. */
  readonly hypotheses?: HypothesesState | null;
  readonly onOpenOperation?: (id: string) => void;
  readonly busy: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}

export function RejectDifferenceDialog(p: RejectDifferenceDialogProps) {
  const { t, locale } = useI18n();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (p.open) setReason('');
  }, [p.open]);
  return (
    <AnimatePresence>
      {p.open && p.summary && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm" data-testid="reject-difference-dialog">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] rounded-[2.5rem] border-2 border-rose-500/30 w-full max-w-md shadow-2xl p-6 md:p-8 text-right space-y-6"
            dir={locale === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center">
                <AlertTriangle size={36} className="text-rose-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-rose-600 dark:text-rose-500">{t('mt.reject_title')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-bold leading-relaxed px-2">{t('mt.reject_hint')}</p>
              </div>
            </div>
            <SummaryBox s={p.summary} />
            {p.hypotheses && <DifferenceHypotheses state={p.hypotheses} {...(p.onOpenOperation ? { onOpenOperation: p.onOpenOperation } : {})} />}
            <div className="space-y-2">
              <label className="block text-xs font-black text-gray-500 dark:text-gray-400">{t('mt.reject_reason')}</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={t('mt.reject_reason_ph')} className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-2xl px-4 py-3 text-xs font-bold dark:text-white" data-testid="reject-reason" />
            </div>
            <div className="flex gap-3">
              <button type="button" disabled={p.busy || !reason.trim()} onClick={() => p.onConfirm(reason.trim())} data-testid="reject-confirm" className="flex-1 bg-rose-600 hover:bg-rose-500 text-white font-black py-3 rounded-2xl text-xs transition-all disabled:opacity-50">
                {t('mt.btn_reject')}
              </button>
              <button type="button" onClick={p.onClose} className="flex-1 bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-gray-300 font-black py-3 rounded-2xl text-xs">
                {t('cancel')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
